// UI gates from the session's permissions (contract §6.4). Cosmetic: every route enforces its own permission at the service.
import { can, getAuthSession } from "../../lib/http/session";
import { User } from "../../types/admin/UserTypes";
import { Role } from "../../types/admin/RoleTypes";
import { RoleGroups } from "../../api/admin/rolesApi";
import { hasFeature } from "../subscriptions/subscriptionService";
import { isCustomerIdentity } from "../customer/customerDirectory";
import { ENV } from "../../config/runtimeEnv";

export { can };

// identity computes the audience from the snapshot roles' `audience` (contract §6.2); it is never derived from a role list here.
export const isStaffSession = (): boolean => getAuthSession().info?.session?.audience === "staff";
// Customers, gene.codes and role-less accounts are patient-audience too, so this is "not staff", not "a patient record".
export const isPatientSession = (): boolean => getAuthSession().info?.session?.audience === "patient";

// The patient portal permission, held by exactly the patient roles (Patient, Guardian/Caregiver, Patient Proxy).
export const isPatientPortalUser = (): boolean => can("healthcare.portal.read");

// A staff work area also needs the staff audience, so a permission granted to a patient-audience role never opens one.
export const staffCan = (permission: string): boolean => isStaffSession() && can(permission);

// ── Areas: each nav entry and its route guard share one predicate ──────────────

export const canViewPatients = (): boolean => staffCan("healthcare.patients.read");
export const canViewAppointments = (): boolean => staffCan("healthcare.appointments.read");
// The communications feed is about patients, so it also needs the patient record.
export const canViewCommunications = (): boolean => canViewPatients() && can("notification.communications.read");
export const canViewFormsInTray = (): boolean => staffCan("healthcare.forms.read");
export const canViewTreatmentPlans = (): boolean => staffCan("healthcare.treatment_plans.read");
export const canViewTestOrders = (): boolean => staffCan("healthcare.test_orders.read");
export const canViewPrescriptionDrafts = (): boolean => staffCan("healthcare.prescriptions.read");
export const canWritePrescriptionDrafts = (): boolean => staffCan("healthcare.prescriptions.write");

export const canViewLabOrders = (): boolean => staffCan("labiq.orders.read");
export const canViewLabProcessing = (): boolean => staffCan("labiq.processing.read");
export const canViewLabTracking = (): boolean => staffCan("labiq.samples.read");

export const canViewPayments = (): boolean => staffCan("finance.payments.read");
export const canViewSubscriptions = (): boolean => staffCan("finance.subscriptions.read");
export const canRaiseInvoices = (): boolean => staffCan("finance.invoices.write");

export const hasInventoryAccess = (): boolean => hasFeature("inventory") && staffCan("commerce.inventory.read");

// gene.codes is held by patient-audience accounts too: the wheel viewer excludes only the patient portal, as before.
export const canUseGeneCodesWheel = (): boolean => can("labiq.genecodes.read") && !isPatientPortalUser();

export const canUseXoeAssistant = (): boolean => can("xoe_agent.conversations.use") && !isPatientPortalUser();

export const canUseResearch = (): boolean => staffCan("healthcare.research.use");

// Feature flags and account type compose with the permission gates; each answers only "may this account use this area".
export const canAccessResearch = (): boolean => ENV.VITE_RESEARCH_ENABLED === "true" && canUseResearch();
export const canAccessXoe = (): boolean => ENV.VITE_XOE_ENABLED === "true" && canUseXoeAssistant() && !isCustomerIdentity();

export const canReviewHiAlerts = (): boolean => can("healthcare.hi_alerts.review");

export const canManageOthersSchedule = (): boolean => can("healthcare.schedules.manage");

// The clinic administration area maps to the decision-13 permission (identity catalogue tightenings §4).
export const canAdministerClinic = (): boolean => staffCan("healthcare.staff.manage");

export const canViewOpsDashboards = (): boolean => can("platform.ops.read");

// The platform operators' repair tools (previously an email allow-list).
export const canUseOperatorTools = (): boolean => can("platform.ops.manage");

// ── Another account's record: classification by its role names, never a gate on the viewer ──

export const hasAnyRoleInGroup = (group: keyof typeof RoleGroups, user: User): boolean => {
	const groupToCheck: string[] = RoleGroups[group];
	return user.roles?.some((role: Role) => groupToCheck.includes(role.name)) || false;
};
